import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import { createSimilarPartFeedback } from "#modules/feedback/infra/createSimilarPartFeedback.api";
import { deleteSimilarPartFeedback } from "#modules/feedback/infra/deleteSimilarPartFeedback.api";
import { updateSimilarPartFeedback } from "#modules/feedback/infra/updateSimilarPartFeedback.api";
import { useSimilarPartFeedbacks } from "#modules/feedback/view/useSimilarPartFeedbacks";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { SimilarPartFeedbackRating } from "shared";

export const useSubmitSimilarPartFeedback = ({
  sourcePartId,
  targetPartId,
}: {
  sourcePartId: string | undefined;
  targetPartId: string;
}) => {
  const queryClient = useQueryClient();
  const { similarPartFeedbacks } = useSimilarPartFeedbacks(sourcePartId);

  const similarPartFeedback = similarPartFeedbacks?.find(
    (feedback) => feedback.targetPartId === targetPartId,
  );

  const invalidateFeedbacks = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.similarPartFeedbacks.bySourcePart(sourcePartId),
    });

  const onError = (err: Error) => {
    errorHandler(err);
    showErrorToast({ title: t("feedback.error"), message: t("feedback.errorMessage") });
  };

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: createSimilarPartFeedback,
    onSuccess: () => {
      void invalidateFeedbacks();
      showSuccessToast({ title: t("feedback.success"), message: t("feedback.thankYou") });
    },
    onError,
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateSimilarPartFeedback,
    onSuccess: () => {
      void invalidateFeedbacks();
      showSuccessToast({ title: t("feedback.updated"), message: t("feedback.thankYou") });
    },
    onError,
  });

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationFn: deleteSimilarPartFeedback,
    onSuccess: () => {
      void invalidateFeedbacks();
      showSuccessToast({ title: t("feedback.deleted"), message: t("feedback.feedbackRemoved") });
    },
    onError,
  });

  const submitFeedback = (rating: SimilarPartFeedbackRating, index: number) => {
    if (!sourcePartId) {
      return;
    }

    if (!similarPartFeedback) {
      create({ sourcePartId, targetPartId, rating, index });

      return;
    }

    if (similarPartFeedback.rating === rating) {
      remove({ feedbackId: similarPartFeedback.id });

      return;
    }

    update({ feedbackId: similarPartFeedback.id, rating, index });
  };

  return {
    similarPartFeedback,
    submitFeedback,
    isSubmitting: isCreating || isUpdating || isRemoving,
  };
};
